import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import * as Models from 'Models/Entities';

interface VehicleCapacityRestriction {
	maxCount?: number;
	maxSpace?: number;
	/**
	 * The error message to show a customer when a restriction causes them to overbook.
	 * The `id` references a translation in the Language entity, allowing the message to be displayed in different languages.
	 */
	errorMessageId?: string;
}

interface CapacityRestrictionConfigurationDto {
	max: number;
	vehicleConfig?: VehicleCapacityRestriction;
}

export interface ICapacityRestrictionEntityAttributes extends IModelAttributes {
	name: string;
	configuration: string;
	capacityTypeId: string;
	capacityType: Models.CapacityTypeEntity | Models.ICapacityTypeEntityAttributes;
	ferries?: Array<Models.FerryCapacityRestriction | Models.IFerryCapacityRestrictionAttributes>;
}

@entity('CapacityRestrictionEntity', 'Capacity Restriction')
export default class CapacityRestrictionEntity extends Model implements ICapacityRestrictionEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<CapacityRestrictionEntity, string>()
	public name: string;

	@observable
	@attribute<CapacityRestrictionEntity, string>()
	public configuration: string;

	@observable
	@attribute()
	public capacityTypeId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public capacityType: Models.CapacityTypeEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public ferries: Models.FerryCapacityRestriction[] = [];

	constructor(attributes?: Partial<ICapacityRestrictionEntityAttributes>) {
		super(attributes);

		this.ferries = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ICapacityRestrictionEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.name)) {
				this.name = attributes.name;
			}
			if (isNotNullOrUndefined(attributes.configuration)) {
				this.configuration = attributes.configuration;
			}
			if (isNotNullOrUndefined(attributes.capacityTypeId)) {
				this.capacityTypeId = attributes.capacityTypeId;
			}
			if (isNotNullOrUndefined(attributes.capacityType)) {
				if (attributes.capacityType === null) {
					this.capacityType = attributes.capacityType;
				} else if (attributes.capacityType instanceof Models.CapacityTypeEntity) {
					this.capacityType = attributes.capacityType;
					this.capacityTypeId = attributes.capacityType.id;
				} else {
					this.capacityType = new Models.CapacityTypeEntity(attributes.capacityType);
					this.capacityTypeId = this.capacityType.id;
				}
			}
			if (attributes.ferries !== undefined && Array.isArray(attributes.ferries)) {
				this.ferries = [];
				for (const model of attributes.ferries) {
					if (model instanceof Models.FerryCapacityRestriction) {
						this.ferries.push(model);
					} else {
						this.ferries.push(new Models.FerryCapacityRestriction(model));
					}
				}
			}
		}
	}

	public getConfigurationJsonAsDto(): CapacityRestrictionConfigurationDto {
		let parsedConfig: any;
		try {
			parsedConfig = JSON.parse(this.configuration);
		} catch (e) {
			console.error('Failed to parse configuration:', e);
			// Handle the error, maybe set a default config
			parsedConfig = {}; // Default empty object
		}

		// The canonical stored JSON is PascalCase (written server-side by Newtonsoft), but some
		// configurations were saved camelCase by the admin CRUD page, so accept both casings.
		const rawVehicleConfig = parsedConfig.VehicleConfig ?? parsedConfig.vehicleConfig;
		const vehicleConfig: VehicleCapacityRestriction | undefined = rawVehicleConfig
			? {
					maxCount: rawVehicleConfig.MaxCount ?? rawVehicleConfig.maxCount ?? undefined,
					maxSpace: rawVehicleConfig.MaxSpace ?? rawVehicleConfig.maxSpace ?? undefined,
					errorMessageId: rawVehicleConfig.ErrorMessageId ?? rawVehicleConfig.errorMessageId ?? undefined,
				}
			: undefined;

		return {
			max: parsedConfig.Max ?? parsedConfig.max,
			vehicleConfig,
		};
	}
}
